import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  DirectionsCar as RidesIcon,
  CardGiftcard as LoyaltyIcon,
  Phone as PhoneIcon,
  Smartphone as SmartphoneIcon,
  Block as BlockIcon,
  CheckCircleOutline as UnblockIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import {
  fetchCustomers,
  deleteCustomer,
  toggleCustomerBlocked,
  startCustomersStatusListener,
  stopCustomersStatusListener
} from '../../redux/slices/customersSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const RidesBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '& .MuiSvgIcon-root': {
    color: theme.palette.info.main,
    fontSize: '1.2rem',
  },
}));

// Einmalig erstellter Formatter – nicht bei jedem formatDate()-Aufruf neu instanziieren
const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const CustomersList = () => {
  const dispatch = useDispatch();
  const { customers, isLoading, error } = useSelector(state => state.customers);
  const { global: globalSettings } = useSelector(state => state.settings);

  // Treuekarten-Einstellungen
  const ridesRequired = globalSettings?.loyaltyCardRidesRequired || 5;
  const loyaltyActive = globalSettings?.loyaltyCardActive || false;
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlineFilter, setOnlineFilter] = useState('all');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const [blockedFilter, setBlockedFilter] = useState('active');
  const [sortBy, setSortBy] = useState('name');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCustomer, setMenuCustomer] = useState(null);
  
  // Flag, um zu verhindern, dass useEffect mehrmals ausgeführt wird
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    dispatch(fetchSettings());

    if (!dataLoaded) {
      const loadCustomers = async () => {
        try {
          await dispatch(fetchCustomers({ simplified: true, forceRefresh: false }));
          setDataLoaded(true);
          dispatch(startCustomersStatusListener());
        } catch (error) {
          console.error("CustomersList: Fehler beim Laden der Kundendaten:", error);
          setDataLoaded(false);
        }
      };
      loadCustomers();
    }

    return () => {
      dispatch(stopCustomersStatusListener());
    };
  }, [dispatch, dataLoaded]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleOnlineFilterChange = useCallback((event) => {
    setOnlineFilter(event.target.value);
    setPage(0);
  }, []);

  const handleCustomerTypeFilterChange = useCallback((event) => {
    setCustomerTypeFilter(event.target.value);
    setPage(0);
  }, []);

  const handleBlockedFilterChange = useCallback((event) => {
    setBlockedFilter(event.target.value);
    setPage(0);
  }, []);

  const handleSortByChange = useCallback((event) => {
    setSortBy(event.target.value);
    setPage(0);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuCustomer(null);
  }, []);

  const handleOpenMenu = useCallback((event, customer) => {
    setMenuAnchor(event.currentTarget);
    setMenuCustomer(customer);
  }, []);

  const handleDeleteClick = useCallback((customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
    setMenuCustomer(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedCustomer) {
      dispatch(deleteCustomer(selectedCustomer.id));
      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
    }
  }, [dispatch, selectedCustomer]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedCustomer(null);
  }, []);

  const handleToggleBlocked = useCallback((customer) => {
    dispatch(toggleCustomerBlocked({ id: customer.id, isBlocked: !customer.isBlocked }));
    setMenuAnchor(null);
    setMenuCustomer(null);
  }, [dispatch]);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'Nie';
    return DATE_FORMATTER.format(new Date(timestamp));
  }, []);

  // Filter und Sort – wird nur neu berechnet wenn customers oder Filterkriterien sich ändern.
  // Echtzeit-Updates vom Kunden-Status-Listener triggern automatisch eine Neuberechnung,
  // da `customers` als Dependency enthalten ist.
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return customers.filter(customer => {
      const searchMatch =
        customer.user?.name?.toLowerCase().includes(term) ||
        customer.user?.email?.toLowerCase().includes(term) ||
        customer.user?.phoneNumber?.includes(searchTerm);
      const onlineMatch =
        onlineFilter === 'all' ||
        (onlineFilter === 'online' && customer.isOnline) ||
        (onlineFilter === 'offline' && !customer.isOnline);
      const customerTypeMatch =
        customerTypeFilter === 'all' ||
        (customerTypeFilter === 'phone' && customer.user?.isPhoneCustomer) ||
        (customerTypeFilter === 'app' && !customer.user?.isPhoneCustomer);
      const blockedMatch =
        blockedFilter === 'all' ||
        (blockedFilter === 'active' && !customer.isBlocked) ||
        (blockedFilter === 'blocked' && customer.isBlocked === true);
      return searchMatch && onlineMatch && customerTypeMatch && blockedMatch;
    }).sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.user?.name || '').localeCompare(b.user?.name || '');
        case 'rides':
          return (b.total_rides || 0) - (a.total_rides || 0);
        case 'lastLogin':
          return (b.lastLogin ? new Date(b.lastLogin).getTime() : 0) -
                 (a.lastLogin ? new Date(a.lastLogin).getTime() : 0);
        default:
          return 0;
      }
    });
  }, [customers, searchTerm, onlineFilter, customerTypeFilter, blockedFilter, sortBy]);

  // Pagination – neu berechnet nur wenn filteredData, page oder rowsPerPage sich ändern
  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );
  
  if (isLoading) {
    return <Loading message="Kunden werden geladen..." />;
  }
  
  if (error) {
    console.error('Fehler in CustomersList Component:', error);
    return (
      <ErrorMessage
        title="Fehler beim Laden der Kunden"
        message={error.message || error.details || JSON.stringify(error)}
        onRetry={() => dispatch(fetchCustomers())}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Kundenverwaltung"
        description="Übersicht und Verwaltung aller Kunden"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Kunden', path: '/kunden' }
        ]}
        actionButton={{
          label: 'Kunde hinzufügen',
          icon: <AddIcon />,
          path: '/kunden/neu'
        }}
      />
      
      {/* Filter und Suche */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche nach Name, Email oder Telefon"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="online-filter-label">Online Status</InputLabel>
              <Select
                labelId="online-filter-label"
                id="online-filter"
                value={onlineFilter}
                label="Online Status"
                onChange={handleOnlineFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="customer-type-filter-label">Kundentyp</InputLabel>
              <Select
                labelId="customer-type-filter-label"
                id="customer-type-filter"
                value={customerTypeFilter}
                label="Kundentyp"
                onChange={handleCustomerTypeFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="phone">Telefonkunden</MenuItem>
                <MenuItem value="app">App-Kunden</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="blocked-filter-label">Kontostatus</InputLabel>
              <Select
                labelId="blocked-filter-label"
                id="blocked-filter"
                value={blockedFilter}
                label="Kontostatus"
                onChange={handleBlockedFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="active">Nur aktive</MenuItem>
                <MenuItem value="blocked">Nur gesperrte</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="sort-by-label">Sortieren nach</InputLabel>
              <Select
                labelId="sort-by-label"
                id="sort-by"
                value={sortBy}
                label="Sortieren nach"
                onChange={handleSortByChange}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="rides">Anzahl der Fahrten</MenuItem>
                <MenuItem value="lastLogin">Zuletzt Online</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {filteredData.length} Kunden
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Kundentabelle */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="Kundentabelle">
          <TableHead>
            <TableRow>
              <TableCell>Kunde</TableCell>
              <TableCell align="center">Online</TableCell>
              <TableCell align="center">Fahrten</TableCell>
              <TableCell align="center">Treuekarte</TableCell>
              <TableCell align="center">Telefon</TableCell>
              <TableCell align="center">Zuletzt Online</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((customer) => (
                <StyledTableRow key={customer.id}>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={customer.user?.profile_picture || ''}
                        sx={{ opacity: customer.isBlocked ? 0.5 : 1 }}
                      >
                        {customer.user?.name ? customer.user.name.charAt(0) : 'K'}
                      </Avatar>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{customer.user?.name}</Typography>
                          <Chip
                            icon={customer.user?.isPhoneCustomer ? <PhoneIcon fontSize="small" /> : <SmartphoneIcon fontSize="small" />}
                            label={customer.user?.isPhoneCustomer ? 'Telefon' : 'App'}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              borderColor: customer.user?.isPhoneCustomer ? 'warning.main' : 'success.main',
                              color: customer.user?.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                              '& .MuiChip-icon': {
                                color: customer.user?.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                                fontSize: '0.8rem',
                              },
                            }}
                          />
                          {customer.isBlocked && (
                            <Chip
                              label="Gesperrt"
                              size="small"
                              icon={<BlockIcon />}
                              color="error"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="textSecondary">{customer.user?.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={customer.isOnline ? "Online" : "Offline"}
                      color={customer.isOnline ? "success" : "default"}
                      size="small"
                      icon={customer.isOnline ? <OnlineIcon /> : <OfflineIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <RidesBox>
                      <RidesIcon />
                      <Typography>{customer.total_rides || 0}</Typography>
                    </RidesBox>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: loyaltyActive ? 'primary.light' : 'grey.200',
                        color: loyaltyActive ? 'primary.contrastText' : 'text.secondary'
                      }}>
                        <LoyaltyIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {customer.loyaltyCard?.currentRideCount || 0}/{ridesRequired}
                        </Typography>
                      </Box>
                      {(customer.loyaltyCard?.availableDiscounts || 0) > 0 && (
                        <Chip
                          size="small"
                          label={`${customer.loyaltyCard.availableDiscounts}x`}
                          color="success"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">{customer.user?.phoneNumber}</TableCell>
                  <TableCell align="center">{formatDate(customer.lastLogin)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`customer-menu-${customer.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, customer)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Kunden gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuCustomer ? `customer-menu-${menuCustomer.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuCustomer ? `/kunden/${menuCustomer.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Kunde anzeigen
        </MenuItem>
        <MenuItem 
          component={Link} 
          to={menuCustomer ? `/kunden/${menuCustomer.id}/bearbeiten` : '#'} 
          onClick={handleCloseMenu}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        <MenuItem
          onClick={() => menuCustomer && handleToggleBlocked(menuCustomer)}
          sx={{ color: menuCustomer?.isBlocked ? 'success.main' : 'warning.main' }}
        >
          {menuCustomer?.isBlocked ? (
            <><UnblockIcon fontSize="small" sx={{ mr: 1 }} />Entsperren</>
          ) : (
            <><BlockIcon fontSize="small" sx={{ mr: 1 }} />Sperren</>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => menuCustomer && handleDeleteClick(menuCustomer)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>
      
      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Kunde löschen"
        content={`Möchten Sie den Kunden ${selectedCustomer?.user?.name || ''} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default CustomersList;